<script lang="ts" setup>
import { UserState } from '@/composition/users'
import { EventsService } from '@/service/EventsService'
import {
  UITextLgMedium,
  UITextMdMedium,
  UITextSmNormal,
} from '@gohighlevel/ghl-ui'
import moment from 'moment-timezone'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SidebarEventsListLoader from './SidebarEventsListLoader.vue'
import { formatEventTimeAndDate } from './helper/EventsHelper'

const events = ref<any>(null)
const loadingEvents = ref<boolean>(false)
const { t } = useI18n()
onMounted(async () => {
  try {
    loadingEvents.value = true
    const localTimezone =
      UserState.state.currentUser?.timeZone || moment.tz.guess()

    // Current time in local timezone with proper timezone info
    const currentTime = moment().tz(localTimezone)

    const fetchedEvents = await EventsService.fetchPastAndUpcomingEvents({
      currentDate: currentTime.clone().utc().format(),
      limit: 6,
    })

    // Merge all events into a single array
    const allEvents = [
      ...(fetchedEvents.pastEvents || []),
      ...(fetchedEvents.upcomingEvents || []),
    ].filter(event => event !== null)

    // Re-categorize events based on current time
    const pastEvents: typeof allEvents = []
    const upcomingEvents: typeof allEvents = []

    allEvents.forEach(event => {
      const eventTimezone = event.timezone || localTimezone

      const localOffset = moment().tz(localTimezone).utcOffset()
      const eventOffset = moment().tz(eventTimezone).utcOffset()

      let eventStartInLocal
      if (localOffset === eventOffset) {
        const zone = moment.tz.zone(eventTimezone)

        if (zone && event.recurringMeta?.eventStart) {
          // Get the offset from when the recurring series started
          const referenceDate = moment.utc(event.recurringMeta.eventStart)
          const offset = zone.utcOffset(referenceDate.valueOf())

          const offsetHours = Math.floor(Math.abs(offset) / 60)
          const offsetMinutes = Math.abs(offset) % 60

          // If offset is negative (west of UTC), add hours; if positive (east of UTC), subtract hours
          const adjustHours = offset < 0 ? offsetHours : -offsetHours
          const adjustMinutes = offset < 0 ? offsetMinutes : -offsetMinutes

          // Create moment in local timezone with historical DST offset
          eventStartInLocal = moment
            .utc(event.startTime)
            .add(adjustHours, 'hours')
            .add(adjustMinutes, 'minutes')
            .tz(localTimezone, true) // true keeps the local time and adjusts the offset
        } else {
          eventStartInLocal = moment.utc(event.startTime).tz(localTimezone)
        }
      } else {
        eventStartInLocal = moment.utc(event.startTime).tz(localTimezone)
      }

      // Compare the unix timestamps to avoid timezone issues
      const isUpcoming = eventStartInLocal.unix() > currentTime.unix()

      if (isUpcoming) {
        upcomingEvents.push(event)
      } else {
        pastEvents.push(event)
      }
    })

    // Sort past events in reverse chronological order (latest first)
    pastEvents.sort((a, b) => {
      const timeA = moment.utc(a.startTime)
      const timeB = moment.utc(b.startTime)
      return timeB.valueOf() - timeA.valueOf() // Reverse order
    })

    events.value = {
      pastEvents: pastEvents.slice(0, 4),
      upcomingEvents: upcomingEvents.slice(0, 4),
    }
  } catch (error) {
    console.error(error)
  } finally {
    loadingEvents.value = false
  }
})
</script>
<template>
  <div class="flex h-container w-full flex-col">
    <div class="mt-5 h-[310px] px-4">
      <div class="mb-5">
        <UITextMdMedium
          v-if="events?.upcomingEvents?.length > 0 || loadingEvents"
          class="text-communities-sidebar-font-primary"
          >{{ t('events.upcomingEvents') }}</UITextMdMedium
        >
        <UITextMdMedium
          v-else-if="!loadingEvents"
          class="text-communities-sidebar-font-primary"
          >{{ t('events.noUpcomingEvents') }}</UITextMdMedium
        >
      </div>
      <div class="flex flex-col gap-6">
        <div
          v-if="
            events?.upcomingEvents?.length === 0 ||
            (!events?.upcomingEvents && !loadingEvents)
          "
          class="flex justify-center"
        >
          <img
            alt="Announce Events"
            src="https://storage.googleapis.com/revex-communities-staging/assets/announce_events.png"
            class="h-[85px] w-[105px] text-communities-sidebar-font-primary"
            style="margin-left: -10px; margin-top: 20px"
          />
        </div>
        <UITextSmNormal
          v-if="
            events?.upcomingEvents?.length === 0 ||
            (!events?.upcomingEvents && !loadingEvents)
          "
          class="w-full text-center text-communities-sidebar-font-primary"
          >Coming up events!</UITextSmNormal
        >
        <div v-if="!loadingEvents">
          <div class="flex flex-col gap-6">
            <div v-for="(event, index) in events?.upcomingEvents" :key="index">
              <div class="flex cursor-default flex-row gap-2">
                <div
                  id="event-date-container"
                  class="flex h-[48px] w-[48px] basis-12 flex-col items-center justify-center gap-0 rounded-md bg-communities-border p-0 text-center"
                >
                  <UITextLgMedium
                    class="text-communities-font-primary"
                    style="line-height: 20px"
                  >
                    {{
                      formatEventTimeAndDate(
                        event.startTime,
                        event.endTime,
                        event
                      ).date
                    }}
                  </UITextLgMedium>
                  <span class="text-communities-font-primary">
                    {{
                      formatEventTimeAndDate(
                        event.startTime,
                        event.endTime,
                        event
                      ).month
                    }}
                  </span>
                </div>
                <div class="flex basis-72 flex-col gap-1">
                  <UITextMdMedium
                    class="line-clamp-1 text-communities-sidebar-font-primary"
                    >{{ event.title }}</UITextMdMedium
                  >
                  <span
                    class="line-clamp-1 flex flex-row gap-1 text-communities-font-secondary"
                  >
                    <UITextSmNormal
                      class="line-clamp-1 text-communities-sidebar-font-primary"
                      >{{
                        formatEventTimeAndDate(
                          event.startTime,
                          event.endTime,
                          event
                        ).timeString
                      }},</UITextSmNormal
                    >
                    <UITextSmNormal
                      class="line-clamp-1 text-communities-sidebar-font-primary"
                      >{{
                        formatEventTimeAndDate(
                          event.startTime,
                          event.endTime,
                          event
                        ).dateString
                      }}</UITextSmNormal
                    >
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div v-for="n in 4" :key="n">
            <div class="flex flex-col gap-4">
              <SidebarEventsListLoader class="pt-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-5 h-[310px] px-4">
      <div class="mb-5">
        <UITextMdMedium
          v-if="events?.pastEvents?.length > 0 || loadingEvents"
          class="text-communities-sidebar-font-primary"
          >{{ t('events.pastEvents') }}</UITextMdMedium
        >
        <UITextMdMedium
          v-else-if="!loadingEvents"
          class="text-communities-sidebar-font-primary"
          >{{ t('events.noPastEvents') }}</UITextMdMedium
        >
      </div>
      <div v-if="!loadingEvents">
        <div class="flex flex-col gap-6">
          <div v-for="(event, index) in events?.pastEvents" :key="index">
            <div class="flex flex-row gap-2">
              <div
                class="flex h-[48px] w-[48px] basis-12 flex-col items-center justify-center gap-0 rounded-md bg-communities-border p-0 text-center"
              >
                <UITextLgMedium
                  class="text-communities-font-primary"
                  style="line-height: 20px"
                >
                  {{
                    formatEventTimeAndDate(
                      event.startTime,
                      event.endTime,
                      event
                    ).date
                  }}
                </UITextLgMedium>
                <span class="text-communities-font-primary">
                  {{
                    formatEventTimeAndDate(
                      event.startTime,
                      event.endTime,
                      event
                    ).month
                  }}
                </span>
              </div>
              <div class="flex basis-72 flex-col gap-1">
                <UITextMdMedium
                  class="line-clamp-1 text-communities-sidebar-font-primary"
                  >{{ event.title }}</UITextMdMedium
                >
                <span
                  class="line-clamp-1 flex flex-row gap-1 text-communities-font-secondary"
                >
                  <UITextSmNormal
                    class="line-clamp-1 text-communities-sidebar-font-primary"
                    >{{
                      formatEventTimeAndDate(
                        event.startTime,
                        event.endTime,
                        event
                      ).timeString
                    }},</UITextSmNormal
                  >
                  <UITextSmNormal
                    class="line-clamp-1 text-communities-sidebar-font-primary"
                    >{{
                      formatEventTimeAndDate(
                        event.startTime,
                        event.endTime,
                        event
                      ).dateString
                    }}</UITextSmNormal
                  >
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-for="n in 4" :key="n">
          <div class="flex flex-col gap-4">
            <SidebarEventsListLoader class="pt-4" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.n-divider:not(.n-divider--vertical) {
  margin-top: 16px;
  margin-bottom: 16px;
  --n-color: var(--sidebar-border) !important;
}
.n-divider:not(.n-divider--dashed) .n-divider__line {
  background-color: var(--sidebar-border);
}
.flex-grow {
  flex-grow: 1;
}
</style>
